import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ChangePasswordProps {
  username: string;
  onSubmit: (currentPassword: string, newPassword: string) => Promise<void>;
  onCancel: () => void;
}

export const ChangePassword: React.FC<ChangePasswordProps> = ({ username, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<ChangePasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    localStorage.removeItem('first_login_' + username);
  }, [username]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.currentPassword) newErrors.currentPassword = 'Senha atual é obrigatória';
    if (!formData.newPassword) {
      newErrors.newPassword = 'Nova senha é obrigatória';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Nova senha deve ter pelo menos 8 caracteres';
    } else if (!/(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'Nova senha deve conter pelo menos uma letra maiúscula e um número';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não conferem';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof ChangePasswordData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      await onSubmit(formData.currentPassword, formData.newPassword);
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setErrors({});
    } catch (error: any) {
      console.error('Erro ao alterar senha:', error);
      setErrors({ submit: error?.message || 'Erro ao alterar senha. Tente novamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    if (!password) return { score: 0, text: '', color: 'text-gray-400' };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score <= 2
      ? { score, text: 'Fraca', color: 'text-red-600' }
      : score <= 3
      ? { score, text: 'Média', color: 'text-yellow-600' }
      : { score, text: 'Forte', color: 'text-green-600' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  const eyeButton = (field: 'current' | 'new' | 'confirm') => (
    <button
      type="button"
      onClick={() => setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }))}
      className="text-gray-400 hover:text-gray-600"
    >
      {showPasswords[field] ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Alterar Senha</h2>
            <p className="text-sm text-gray-600 mt-1">
              Usuário: <span className="font-medium">{username}</span>
            </p>
          </div>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <TextInput
            label="Senha Atual"
            type={showPasswords.current ? 'text' : 'password'}
            icon={<Lock className="h-5 w-5" />}
            value={formData.currentPassword}
            onChange={(e) => handleInputChange('currentPassword', e.target.value)}
            error={errors.currentPassword}
            autoComplete="current-password"
            rightElement={eyeButton('current')}
          />

          <div>
            <TextInput
              label="Nova Senha"
              type={showPasswords.new ? 'text' : 'password'}
              icon={<Lock className="h-5 w-5" />}
              value={formData.newPassword}
              onChange={(e) => handleInputChange('newPassword', e.target.value)}
              error={errors.newPassword}
              autoComplete="new-password"
              rightElement={eyeButton('new')}
            />
            {formData.newPassword && (
              <div className="mt-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Força da senha:</span>
                  <span className={`text-xs font-medium ${passwordStrength.color}`}>{passwordStrength.text}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className={`h-2 rounded-full transition-all duration-300 ${
                    passwordStrength.score <= 2 ? 'bg-red-500 w-1/3' :
                    passwordStrength.score <= 3 ? 'bg-yellow-500 w-2/3' : 'bg-green-500 w-full'
                  }`} />
                </div>
              </div>
            )}
          </div>

          <TextInput
            label="Confirmar Nova Senha"
            type={showPasswords.confirm ? 'text' : 'password'}
            icon={<Lock className="h-5 w-5" />}
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            error={errors.confirmPassword}
            autoComplete="new-password"
            rightElement={eyeButton('confirm')}
          />

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Requisitos da senha:</h4>
            <ul className="space-y-1 text-xs text-gray-600">
              {[
                { met: formData.newPassword.length >= 8, text: 'Pelo menos 8 caracteres' },
                { met: /[A-Z]/.test(formData.newPassword), text: 'Pelo menos uma letra maiúscula' },
                { met: /[0-9]/.test(formData.newPassword), text: 'Pelo menos um número' },
              ].map(({ met, text }) => (
                <li key={text} className="flex items-center">
                  <CheckCircle className={`w-3 h-3 mr-2 ${met ? 'text-green-500' : 'text-gray-400'}`} />
                  {text}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onCancel} className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" disabled={isLoading}>
              Cancelar
            </button>
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" disabled={isLoading}>
              {isLoading ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white border-t-transparent mr-2 inline-block" />Alterando...</>
              ) : 'Alterar Senha'}
            </button>
          </div>

          {errors.submit && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.submit}
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
